import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Waves, Mail, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authAPI, bookingAPI } from '../services/api';
import { toast } from 'sonner';

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        // FastAPI-Users login returns { access_token, token_type }
        const loginRes = await authAPI.login({
          email: formData.email,
          password: formData.password,
        });
        const token = loginRes.data.access_token;

        // Temporarily set token so getCurrentUser can authenticate
        localStorage.setItem('token', token);
        try {
          const userRes = await authAPI.getCurrentUser();
          const userData = userRes.data;
          onLogin(userData, token);
          toast.success('Login successful!');

          if (userData.role === 'admin') {
            navigate('/admin');
          } else if (userData.role === 'coach') {
            navigate('/coach');
          } else {
            // Send to portal if they have bookings, otherwise to booking page
            try {
              const res = await bookingAPI.getAll();
              const mine = (res.data.bookings || []).filter(
                (b) => b.student_email === userData.email || b.parent_email === userData.email
              );
              navigate(mine.length > 0 ? '/member-portal' : '/book-lesson');
            } catch {
              navigate('/book-lesson');
            }
          }
        } catch {
          // Profile fetch failed — remove token to avoid zombie auth state
          localStorage.removeItem('token');
          throw new Error('Logged in but could not load your profile. Please try again.');
        }
      } else {
        // FastAPI-Users register returns the user object (no token)
        await authAPI.register({
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name,
          phone: formData.phone,
        });

        // Auto-login after registration
        const loginRes = await authAPI.login({
          email: formData.email,
          password: formData.password,
        });
        const token = loginRes.data.access_token;

        localStorage.setItem('token', token);
        try {
          const userRes = await authAPI.getCurrentUser();
          onLogin(userRes.data, token);
          toast.success('Registration successful!');
          navigate('/book-lesson');
        } catch {
          localStorage.removeItem('token');
          throw new Error('Account created! Please sign in manually.');
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
      const detail = error.response?.data?.detail;
      const friendlyMessages = {
        LOGIN_BAD_CREDENTIALS: 'Incorrect email or password. Please try again.',
        LOGIN_USER_NOT_VERIFIED: 'Your account has not been verified yet.',
        REGISTER_USER_ALREADY_EXISTS: 'An account with this email already exists.',
      };
      let message = error.message || 'Authentication failed. Please try again.';
      if (typeof detail === 'string') message = friendlyMessages[detail] || detail;
      else if (Array.isArray(detail)) message = detail.map(d => d.msg || d).join(', ');
      else if (detail?.reason) message = detail.reason;
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-cyan-50 to-slate-100 p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-cyan-700 rounded-full mb-4">
              <Waves className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              {isLogin ? 'Welcome Back' : 'Join Us'}
            </h1>
            <p className="text-slate-600">
              {isLogin ? 'Sign in to your account' : 'Create your account'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <>
                <div>
                  <Label htmlFor="full_name">Full Name</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => handleChange('full_name', e.target.value)}
                    required={!isLogin}
                    data-testid="full-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    required={!isLogin}
                    data-testid="phone-input"
                  />
                </div>
              </>
            )}

            <div>
              <Label htmlFor="email">
                <Mail className="inline w-4 h-4 mr-2" />
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password">
                <Lock className="inline w-4 h-4 mr-2" />
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                required
                data-testid="password-input"
              />
            </div>

            {isLogin && (
              <div className="text-right -mt-1">
                <Link
                  to="/forgot-password"
                  className="text-sm text-cyan-700 hover:text-cyan-800"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
            )}

            <Button
              type="submit"
              className="w-full btn-primary py-6"
              disabled={loading}
              data-testid="submit-auth-btn"
            >
              {loading ? 'Processing...' : isLogin ? 'Sign In' : 'Sign Up'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-cyan-700 hover:text-cyan-800 font-medium"
              data-testid="toggle-auth-btn"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>

          <div className="mt-6 text-center text-sm text-slate-500">
            <p>Admin login: Use admin credentials provided by Streamline Aquatics</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
